import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Avatar } from '@/header/Avatar';
import { formatRelativeTime } from '~/utils/dateFormat';

interface NoteItemProps {
  name: string;
  content: string;
  /** ISO instant from the API, not a `Date`: this row renders it relatively. */
  createdAt: string;
  /**
   * `null` when the author's user row is gone, since deleting a user doesn't
   * delete their notes. Both links are dropped then, and the caller passes
   * `t('actions.deleted-user')` as `name`.
   */
  authorId: number | null;
  avatarUrl: string;
  onDelete: () => void;
}

export function NoteItem({
  name,
  content,
  createdAt,
  authorId,
  avatarUrl,
  onDelete,
}: NoteItemProps) {
  const { t, i18n } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);

  // No confirmation dialog, matching legacy.
  const handleDelete = () => {
    // Re-entry guard: the card only fades during the delay, and `opacity: 0`
    // doesn't stop clicks, so a second click would send a second DELETE whose
    // 404 paints an error over a delete that worked.
    if (isDeleting) return;
    setIsDeleting(true);
    setTimeout(() => {
      onDelete();
    }, 300);
  };

  const avatar = <Avatar size={36} src={avatarUrl} />;
  const authorLabel = <NoteAuthor>{name}</NoteAuthor>;
  // The author's name is a link inside the sentence, as in legacy.
  // `formatString` returns a string and can't carry a node, so the template is
  // split on its placeholder and the link rendered in the gap.
  const [before, after] = t('wbs.s-left-a-note').split('%s');

  return (
    <NoteCard $isDeleting={isDeleting}>
      <NoteHeader>
        {/* Legacy's avatar link was broken (an empty `href`); here the
           avatar links to the profile like the name does. */}
        {authorId === null ? (
          avatar
        ) : (
          <AvatarLink>
            <Link
              to="/members/$memberId/profile/"
              params={{ memberId: String(authorId) }}
            >
              {avatar}
            </Link>
          </AvatarLink>
        )}
        <NoteAuthorInfo>
          <NoteLabel>
            {before}
            {authorId === null ? (
              authorLabel
            ) : (
              <AuthorLink>
                <Link
                  to="/members/$memberId/profile/"
                  params={{ memberId: String(authorId) }}
                >
                  {authorLabel}
                </Link>
              </AuthorLink>
            )}
            {after}
          </NoteLabel>
          <NoteTime>{formatRelativeTime(createdAt, i18n.language)}</NoteTime>
        </NoteAuthorInfo>
      </NoteHeader>
      <NoteContent>{content}</NoteContent>
      {/* Shown on every note to every viewer the tab admits; the server
         enforces the rule in `delete_student_note`. */}
      <DeleteButton onClick={handleDelete} disabled={isDeleting}>
        <FontAwesomeIcon icon={faTrashAlt} />
        <DeleteText>{t('actions.delete')}</DeleteText>
      </DeleteButton>
    </NoteCard>
  );
}

const NoteCard = styled.div<{ $isDeleting: boolean }>`
  background: var(--white);
  border-radius: 5px;
  box-shadow: var(--shadow-inset-sm);
  opacity: ${(props) => (props.$isDeleting ? 0 : 1)};
  padding: 10px 10px 17px;
  transition: opacity 0.3s ease-out;
`;

const NoteHeader = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  margin-bottom: 17px;
`;

const NoteAuthorInfo = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  gap: 5px;
`;

const NoteAuthor = styled.span`
  color: var(--grey-900);
  font-size: 14px;
`;

// The anchor stays a raw `Link` with the styling on a wrapper: `styled(Link)`
// erases the router's route-aware typing, so a `params` object is rejected.
const AvatarLink = styled.span`
  display: flex;

  a {
    display: flex;
  }
`;

const AuthorLink = styled.span`
  a {
    text-decoration: none;
  }

  a:hover ${NoteAuthor},
  a:focus ${NoteAuthor} {
    color: var(--wbs-red);
  }
`;

const NoteLabel = styled.span`
  color: var(--grey-600);
  font-size: 14px;
`;

const NoteTime = styled.span`
  color: var(--grey-500);
  font-size: 12px;
  font-weight: 300;
`;

const NoteContent = styled.div`
  color: var(--grey-800);
  font-size: 15px;
  margin-bottom: 15px;
  margin-left: 50px;
  white-space: pre-wrap;
`;

const DeleteText = styled.span`
  color: var(--grey-600);
  font-family: var(--raleway);
  font-size: 13px;
  font-weight: 400;
  transition: color 0.2s;
`;

const DeleteButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  gap: 6px;
  padding: 0;

  svg {
    color: var(--wbs-red);
    font-size: 14px;
  }

  &:hover {
    ${DeleteText} {
      color: var(--wbs-red);
    }
  }

  &:disabled {
    cursor: default;
  }
`;
